import React, { Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import styled from 'styled-components';

import { Button, Text } from '@tradingblock/components';
import { useData, useStateSelector } from '../../../state/selectors';
import { ClearerType, Groups, TransferDirections, Transfers } from '@tradingblock/types';
import {
  AchRelationshipFundingMethods,
  FundYourAccountView,
  FundYourAccountViews,
  AchRelationshipFundingMethod,
} from '../FundYourAccount';
import { DefaultApplicationModel } from '../../../types';

const StyledFundingMethods = styled.div`
  .accordion > .card {
    background-color: transparent;
    border: var(--field-border);
    border-radius: var(--corner-radius);
    overflow: visible;
    margin-bottom: 0.75rem;
    transition: all 0.2s ease-in-out;
  }

  .accordion > .card:last-child {
    margin-bottom: 0;
  }

  .accordion > .card:first-of-type {
    border-bottom-right-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
  }

  .accordion > .card:not(:first-of-type):not(:last-of-type) {
    border-radius: 0.25rem;
  }

  .accordion > .card:last-of-type {
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
  }

  .accordion > .card:not(:first-of-type):not(:last-of-type) {
    border-bottom: var(--field-border);
  }

  .accordion > .card .card-header {
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.25rem;
    background-color: var(--field-bg);
    border: var(--field-border);
    color: var(--text);
    transition: all 0.2s ease-in-out;
    font-weight: 500;
    position: relative;
    margin: 0;
    border-radius: 0;
  }

  .accordion > .card:first-of-type .card-header {
    border-top-left-radius: var(--corner-radius);
    border-top-right-radius: var(--corner-radius);
  }

  .accordion > .card:last-of-type .card-header {
    border-bottom-left-radius: var(--corner-radius);
    border-bottom-right-radius: var(--corner-radius);
  }

  .accordion > .card .card-header:hover {
    background-color: var(--blue-dark-lighten);
    border-color: var(--teal);
    z-index: 1;
  }

  .accordion > .card .card-header i {
    color: var(--teal);
    font-size: 0.875rem;
    transition: transform 0.2s ease-in-out;
  }

  .accordion > .card .card-body {
    background-color: var(--field-bg);
    border-top: var(--field-border);
    padding: 1.25rem;
    color: var(--text);
  }

  .sub-header {
    margin-bottom: 1.75rem;
  }

  ul {
    margin-left: 20px;
    list-style: disc;
  }

  .link-bank-account-card-body {
    display: flex;
    flex-direction: column;
  }

  .link-bank-account-card-body button {
    align-self: flex-end;
  }

  .link-bank-account-divider {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .link-bank-account-divider {
    hr {
      width: 45%;
      color: var(--text);
      background: var(--text);
    }
  }

  .loading-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

enum FundingMethodsEnum {
  linkBankAccount = 'linkBankAccount',
  wireTransfer = 'wireTransfer',
  paperCheck = 'paperCheck',
  accountTransfer = 'accountTransfer',
}

type FundingMethodType =
  | FundingMethodsEnum.linkBankAccount
  | FundingMethodsEnum.wireTransfer
  | FundingMethodsEnum.paperCheck
  | FundingMethodsEnum.accountTransfer;

interface FundingMethodsProps {
  setCurrentFundYourAccountView: Dispatch<SetStateAction<FundYourAccountView>>;
  setSelectedAchRelationshipFundingMethod: Dispatch<SetStateAction<AchRelationshipFundingMethod | null>>;
  transfersDataFromCashieringProfile: Transfers | undefined;
  inProgressFundingRequestViaLinkedBankAccount: boolean;
  submittedFundingRequestViaLinkedBankAccount: boolean;
}

const FundingMethods: FunctionComponent<FundingMethodsProps> = ({
  setCurrentFundYourAccountView,
  setSelectedAchRelationshipFundingMethod,
  transfersDataFromCashieringProfile,
  inProgressFundingRequestViaLinkedBankAccount,
  submittedFundingRequestViaLinkedBankAccount,
}) => {
  const [selectedAccordionToggleEventKey, setSelectedAccordionToggleEventKey] = useState<FundingMethodType | null>(
    null
  );
  const [showPaperCheckAccordion, setShowPaperCheckAccordion] = useState<boolean>(false);
  const [showWireTransferAccordion, setShowWireTransferAccordion] = useState<boolean>(false);
  const [showLinkBankAccountAccordion, setShowLinkBankAccountAccordion] = useState<boolean>(false);

  const application = useData(d => d.data.application);

  const siteGrp = useStateSelector(s => s.ui.siteGrp);

  const { clearer } = application || DefaultApplicationModel;

  const domain = siteGrp === Groups.mb ? 'moneyblock' : 'tradingblock';

  useEffect(() => {
    if (transfersDataFromCashieringProfile) {
      if (
        transfersDataFromCashieringProfile.ach &&
        transfersDataFromCashieringProfile.ach.includes(TransferDirections.Incoming)
      ) {
        setShowLinkBankAccountAccordion(true);
      }

      if (transfersDataFromCashieringProfile.check && transfersDataFromCashieringProfile.check.length >= 0) {
        setShowPaperCheckAccordion(true);
      }

      if (transfersDataFromCashieringProfile.wire && transfersDataFromCashieringProfile.wire.length >= 0) {
        setShowWireTransferAccordion(true);
      }
    }
  }, [transfersDataFromCashieringProfile]);

  return (
    <StyledFundingMethods>
      <p className="sub-header">
        <Text id="fundYourAccountComponent.getYourAccountFunded" />
      </p>

      <Accordion
        onSelect={selectedEventKey =>
          setSelectedAccordionToggleEventKey((selectedEventKey as unknown) as FundingMethodType)
        }
      >
        {showLinkBankAccountAccordion && (
          <Card>
            <Accordion.Toggle as={Card.Header} eventKey={FundingMethodsEnum.linkBankAccount}>
              <Text id="fundYourAccountComponent.linkBankAccount" />
              {selectedAccordionToggleEventKey === FundingMethodsEnum.linkBankAccount && (
                <i className="far fa-chevron-up" />
              )}
              {selectedAccordionToggleEventKey !== FundingMethodsEnum.linkBankAccount && (
                <i className="far fa-chevron-down" />
              )}
            </Accordion.Toggle>
            <Accordion.Collapse eventKey={FundingMethodsEnum.linkBankAccount}>
              <Card.Body className="link-bank-account-card-body">
                {inProgressFundingRequestViaLinkedBankAccount && !submittedFundingRequestViaLinkedBankAccount && (
                  <>
                    <p>
                      <Text id="fundYourAccountComponent.looksLikeThisFundingMethodSetupHasBeenStarted" />
                    </p>
                    <Button
                      className="btn-primary"
                      onClick={() => {
                        setCurrentFundYourAccountView(FundYourAccountViews.FundingMethodSetupRequestStep);
                        setSelectedAchRelationshipFundingMethod(null);
                      }}
                    >
                      <Text id="btns.continue" />
                    </Button>
                  </>
                )}

                {submittedFundingRequestViaLinkedBankAccount && (
                  <>
                    <p>
                      <Text id="fundYourAccountComponent.looksLikeAFundingRequestHasBeenSubmitted" />
                    </p>
                    <Button
                      className="btn-primary"
                      onClick={() => {
                        setCurrentFundYourAccountView(FundYourAccountViews.FundingRequestStep);
                      }}
                    >
                      <Text id="btns.review" />
                    </Button>
                  </>
                )}

                {!inProgressFundingRequestViaLinkedBankAccount && !submittedFundingRequestViaLinkedBankAccount && (
                  <>
                    <p>
                      <Text id="fundYourAccountComponent.useYourBankLogin" />
                    </p>
                    <p>
                      <Text id="fundYourAccountComponent.worksForMostUsBanks" />
                    </p>
                    <Button
                      className="btn-primary"
                      onClick={() => {
                        setCurrentFundYourAccountView(FundYourAccountViews.FundingMethodSetupRequestStep);
                        setSelectedAchRelationshipFundingMethod(AchRelationshipFundingMethods.AchRelationshipViaPlaid);
                      }}
                    >
                      <Text id="btns.getStarted" />
                    </Button>

                    {clearer !== ClearerType.RQD && (
                      <>
                        <div className="link-bank-account-divider">
                          <hr />
                          <Text id="or" />
                          <hr />
                        </div>

                        <p>
                          <Text id="fundYourAccountComponent.verifyThroughSmallDeposits" />
                        </p>
                        <p>
                          <Text id="fundYourAccountComponent.worksForAnyBankInUs" />
                        </p>
                        <Button
                          className="btn-primary"
                          onClick={() => {
                            setCurrentFundYourAccountView(FundYourAccountViews.FundingMethodSetupRequestStep);
                            setSelectedAchRelationshipFundingMethod(
                              AchRelationshipFundingMethods.AchRelationshipViaMicroDeposits
                            );
                          }}
                        >
                          <Text id="btns.getStarted" />
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        )}

        {showWireTransferAccordion && (
          <Card>
            <Accordion.Toggle as={Card.Header} eventKey={FundingMethodsEnum.wireTransfer}>
              <Text id="fundYourAccountComponent.wireTransfer" />
              {selectedAccordionToggleEventKey === FundingMethodsEnum.wireTransfer && (
                <i className="far fa-chevron-up" />
              )}
              {selectedAccordionToggleEventKey !== FundingMethodsEnum.wireTransfer && (
                <i className="far fa-chevron-down" />
              )}
            </Accordion.Toggle>
            <Accordion.Collapse eventKey={FundingMethodsEnum.wireTransfer}>
              <Card.Body>
                <Text id="fundYourAccountComponent.sharedRqdAndApexWireTransferDepositInstructions" />
                {clearer === ClearerType.RQD && (
                  <Text
                    id="fundYourAccountComponent.rqdWireTransferDepositInstructions"
                    data={{ domain }}
                    siteGroup={siteGrp}
                  />
                )}
                {clearer === ClearerType.Apex && (
                  <Text id="fundYourAccountComponent.apexWireTransferDepositInstructions" siteGroup={siteGrp} />
                )}
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        )}

        {showPaperCheckAccordion && (
          <Card>
            <Accordion.Toggle as={Card.Header} eventKey={FundingMethodsEnum.paperCheck}>
              <Text id="fundYourAccountComponent.paperCheck" />
              {selectedAccordionToggleEventKey === FundingMethodsEnum.paperCheck && <i className="far fa-chevron-up" />}
              {selectedAccordionToggleEventKey !== FundingMethodsEnum.paperCheck && (
                <i className="far fa-chevron-down" />
              )}
            </Accordion.Toggle>
            <Accordion.Collapse eventKey={FundingMethodsEnum.paperCheck}>
              <Card.Body>
                <Text id="fundYourAccountComponent.checkDepositInstructions" siteGroup={siteGrp} />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        )}

        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={FundingMethodsEnum.accountTransfer}>
            <Text id="fundYourAccountComponent.accountTransfer" />
            {selectedAccordionToggleEventKey === FundingMethodsEnum.accountTransfer && (
              <i className="far fa-chevron-up" />
            )}
            {selectedAccordionToggleEventKey !== FundingMethodsEnum.accountTransfer && (
              <i className="far fa-chevron-down" />
            )}
          </Accordion.Toggle>
          <Accordion.Collapse eventKey={FundingMethodsEnum.accountTransfer}>
            <Card.Body>
              <p>
                <Text id="fundYourAccountComponent.accountTransferInstructions" data={{ emailDomain: domain }} />
              </p>

              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://legacy.${domain}.com/Admin/Forms/${
                  clearer === ClearerType.RQD
                    ? 'RQD/Cashiering/RQD_ACAT.pdf'
                    : 'Apex/Account_Transfer/ACAT_Transfer.pdf'
                }`}
              >
                <Text id="fundYourAccountComponent.clickHereToOpenAccountTransferForm" />
              </a>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </StyledFundingMethods>
  );
};

export default FundingMethods;
